import { useMemo, useState } from 'react'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Input } from '@/shared/components/ui/input'
import { useBusinessCategories } from '@/features/businesses/hooks/useBusinessCategories'
import { cn } from '@/shared/utils/cn'

interface CategoriesSelectProps {
  value: number[]
  onChange: (ids: number[]) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  /** Si `true`, el control se marca como inválido (aria-invalid). */
  invalid?: boolean
}

/**
 * MultiSelect de categorías. Trigger estilo Select + Popover con búsqueda y
 * checkboxes. Las categorías seleccionadas se muestran como chips removibles
 * debajo del trigger.
 */
export function CategoriesSelect({
  value,
  onChange,
  disabled,
  placeholder = 'Selecciona categorías',
  className,
  invalid,
}: CategoriesSelectProps) {
  const { categories, loading, error } = useBusinessCategories()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return categories
    return categories.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    )
  }, [categories, search])

  const selectedSet = useMemo(() => new Set(value), [value])

  const toggle = (id: number) => {
    if (selectedSet.has(id)) {
      onChange(value.filter((v) => v !== id))
    } else {
      onChange([...value, id])
    }
  }

  const remove = (id: number) => {
    onChange(value.filter((v) => v !== id))
  }

  const clearAll = () => onChange([])

  const selectedCategories = useMemo(
    () => categories.filter((c) => selectedSet.has(c.id)),
    [categories, selectedSet],
  )

  if (error) {
    return <p className="text-xs text-danger">No pudimos cargar las categorías.</p>
  }

  return (
    <div className={cn('space-y-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          type="button"
          disabled={disabled || loading}
          aria-invalid={invalid}
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-line bg-panel px-3 text-sm text-ink',
            'transition-colors focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:ring-danger/40',
          )}
        >
          <span className={cn('truncate', value.length === 0 && 'text-ink-subtle')}>
            {loading
              ? 'Cargando categorías…'
              : value.length === 0
                ? placeholder
                : `${value.length} ${value.length === 1 ? 'seleccionada' : 'seleccionadas'}`}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted" />
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
          <div className="relative border-b border-line p-2">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-subtle" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="h-8 pl-8 text-xs"
              autoFocus
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-ink-muted">
                {categories.length === 0
                  ? 'No hay categorías disponibles'
                  : 'Sin resultados'}
              </p>
            ) : (
              filtered.map((c) => {
                const selected = selectedSet.has(c.id)
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggle(c.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                      'hover:bg-surface focus-visible:bg-surface focus-visible:outline-none',
                    )}
                    aria-pressed={selected}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        selected
                          ? 'border-brand bg-brand text-white'
                          : 'border-line bg-panel',
                      )}
                    >
                      {selected && <Check className="h-3 w-3" strokeWidth={3} />}
                    </span>
                    <span className="flex-1 truncate text-ink">{c.name}</span>
                  </button>
                )
              })
            )}
          </div>
          {value.length > 0 && (
            <div className="flex items-center justify-between gap-2 border-t border-line p-2 text-xs">
              <span className="text-ink-muted">{value.length} seleccionadas</span>
              <button
                type="button"
                onClick={clearAll}
                className="font-medium text-brand hover:text-brand-dark"
              >
                Limpiar
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {selectedCategories.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedCategories.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1 rounded-full border border-brand/30 bg-brand-soft px-2.5 py-1 text-xs font-medium text-brand-dark"
            >
              {c.name}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(c.id)}
                  className="-mr-0.5 rounded-full p-0.5 hover:bg-brand/20 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand"
                  aria-label={`Quitar ${c.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
